import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as queryString from 'querystring';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { Modal } from 'react-bootstrap';

import { useBeforeUnload, useSubscriptionLimit, FEATURES } from '../../../../hooks';
import { renderShowConfirmationModal, onHideHandler, InfoModal, Tabs } from '../../../../components';
import { actions as usersActions, constants as userConstants, selectors as usersSelectors } from '../../../../redux/users';
import { constants as subscriptionConstants } from '../../../../redux/subscriptions';
import { selectors as customerSelectors } from '../../../../redux/customers';
import { makeGet } from '../../../../api/ajax';

import UserDetails from './containers/UserDetails';
import Pagination from './containers/Pagination';

const UsersTable = ({ id, onHide, show }) => {
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [page, setPage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allowedRoles, setAllowedRoles] = useState([]);
  const [activeTab, setActiveTab] = useState('My users');
  const [representatives, setRepresentatives] = useState(null);
  const [representativesPage, setRepresentativesPage] = useState(1);
  const [representativesPageCount, setRepresentativesPageCount] = useState(0);

  const dispatch = useDispatch();
  const users = useSelector(usersSelectors.selectUsers);
  const customerDetails = useSelector(customerSelectors.selectCustomerDetails);
  const representativeCustomerName = customerDetails?.companyName || customerDetails?.name || 'this customer';

  const { isFeatureDisabled: usersMaxReached } = useSubscriptionLimit(FEATURES.accountLimit);
  useBeforeUnload(hasUnsavedChanges);

  const changePage = (nextPage) => {
    const recentlyAddedUser = Object.keys(users[page]).length > userConstants.PAGE_SIZE;

    if (recentlyAddedUser) {
      dispatch(usersActions.clearUsers());
    }

    if (nextPage > users.pageCount || nextPage < 1) {
      return;
    }

    setPage(nextPage);

    if (users[nextPage]) {
      return;
    }

    apiGetUsers(nextPage);
  };

  const apiGetUsers = useCallback(
    (page) => {
      const queryParams = queryString.stringify({
        page,
        pageSize: userConstants.PAGE_SIZE,
        customerId: id,
      });

      setIsLoadingUsers(true);
      makeGet(`users?${queryParams}`).subscribe(({ response }) => {
        setIsLoadingUsers(false);

        dispatch(usersActions.loadUsersSuccess(response.results, response.page, response.pageCount));
      }, () => {
        setIsLoadingUsers(false);
      });
    },
    [id, dispatch],
  );

  const apiGetRepresentatives = useCallback(
    (page) => {
      const queryParams = queryString.stringify({
        page,
        pageSize: userConstants.PAGE_SIZE,
      });

      setIsLoadingUsers(true);
      makeGet(`customerRepresentatives?${queryParams}`).subscribe(({ response }) => {
        setIsLoadingUsers(false);
        setRepresentatives(response.results || []);
        setRepresentativesPageCount(response.pageCount || 0);
      }, () => {
        setIsLoadingUsers(false);
      });
    },
    [],
  );

  useEffect(() => {
    makeGet('users/allowedRoles').subscribe(({ response }) => setAllowedRoles(response));
  }, []);

  useEffect(() => {
    if (activeTab === 'My users') {
      if (users) return;
      apiGetUsers(1);
    } else if (activeTab === 'Representatives') {
      if (representatives) return;
      apiGetRepresentatives(1);
    }
  }, [users, representatives, apiGetUsers, apiGetRepresentatives, activeTab]);

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setPage(1);
    setRepresentativesPage(1);
  };

  const changeRepresentativesPage = (nextPage) => {
    if (nextPage > representativesPageCount || nextPage < 1) {
      return;
    }

    setRepresentativesPage(nextPage);
    apiGetRepresentatives(nextPage);
  };

  const handleRepresentativeAdded = () => {
    setRepresentativesPage(1);
    apiGetRepresentatives(1);
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, onHide)}
      <Modal
        id={id}
        size="lg"
        backdrop="static"
        show={show}
        onHide={() => onHideHandler(hasUnsavedChanges, onHide, setShowConfirmModal)}
        centered
      >
        <Modal.Header closeButton>
          <h6>{activeTab === 'My users' ? 'Access management' : 'Representatives management'}</h6>
          <hr />
        </Modal.Header>
        <Modal.Body>
          <div className="row no-gutters">
            <div className="col">
              {usersMaxReached && (
                <InfoModal
                  type="warning"
                  message={subscriptionConstants.SUBSCRIPTION_MESSAGES.cannotAddAccounts}
                  shownModal={usersMaxReached}
                />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col">
              {showSuccessMessage && (
                <div className="alert alert-success" role="alert">
                  {showSuccessMessage}
                  <button type="button" className="close" onClick={() => setShowSuccessMessage(null)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}
              <table className="table table-borderless m-0">
                <tbody>
                  <UserDetails
                    isLoadingUsers={isLoadingUsers}
                    showAlert={setShowSuccessMessage}
                    setPage={activeTab === 'My users' ? setPage : setRepresentativesPage}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setHasUnsavedChanges={(hasUnsavedChanges) => setHasUnsavedChanges(hasUnsavedChanges)}
                    hasReachedSubscriptionLimit={usersMaxReached}
                    allowedRoles={allowedRoles}
                    activeTab={activeTab}
                    onRepresentativeAdded={handleRepresentativeAdded}
                    isRepresentative={activeTab === 'Representatives'}
                  />
                  {!isLoadingUsers && (
                    <tr>
                      <td colSpan={3} style={{ padding: '0.75rem 0' }}>
                        <Tabs tabs={['My users', 'Representatives']} activeTab={activeTab} onTabClick={handleTabClick} />
                      </td>
                    </tr>
                  )}
                  {activeTab === 'My users' && users
                    && _.map(users[page], (u, i) =>
                      <UserDetails
                        isLoadingUsers={isLoadingUsers}
                        user={u}
                        editUser={setUserToEdit}
                        showAlert={setShowSuccessMessage}
                        edit={userToEdit === u.id}
                        cancel={() => setUserToEdit(null)}
                        page={page}
                        key={i}
                        setPage={setPage}
                        hasUnsavedChanges={hasUnsavedChanges}
                        setHasUnsavedChanges={(hasUnsavedChanges) => setHasUnsavedChanges(hasUnsavedChanges)}
                        allowedRoles={allowedRoles}
                        activeTab={activeTab}
                      />)}
                  {activeTab === 'Representatives' && representatives && representatives.map((rep, i) => (
                    <UserDetails
                      key={i}
                      isLoadingUsers={isLoadingUsers}
                      user={rep}
                      editUser={setUserToEdit}
                      showAlert={setShowSuccessMessage}
                      edit={userToEdit === rep.id}
                      cancel={() => setUserToEdit(null)}
                      page={representativesPage}
                      setPage={setRepresentativesPage}
                      hasUnsavedChanges={hasUnsavedChanges}
                      setHasUnsavedChanges={(hasUnsavedChanges) => setHasUnsavedChanges(hasUnsavedChanges)}
                      allowedRoles={allowedRoles}
                      activeTab={activeTab}
                      isRepresentative
                      onRepresentativeAdded={handleRepresentativeAdded}
                    />
                  ))}

                  {activeTab === 'Representatives'
                    && !isLoadingUsers
                    && Array.isArray(representatives)
                    && representatives.length === 0
                    && (
                      <tr>
                        <td colSpan={3} className="text-center text-muted" style={{ padding: '1rem 0' }}>
                          {`No users are currently authorized to represent ${representativeCustomerName}.`}
                        </td>
                      </tr>
                    )}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ padding: '.75rem 0 0 0' }}>
                      {activeTab === 'My users' && <Pagination page={page} changePage={changePage} users={users} />}
                      {activeTab === 'Representatives'
                        && Array.isArray(representatives)
                        && representatives.length > 0
                        && representativesPageCount > 1
                        && (
                          <Pagination
                            page={representativesPage}
                            changePage={changeRepresentativesPage}
                            users={{ pageCount: representativesPageCount }}
                          />
                        )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

UsersTable.propTypes = {
  id: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default UsersTable;
